import type { AttendantStatus, ChatWithLastAttendance } from "@/types";
import type {
  StatusTabValue,
  TabValue,
} from "@/features/chat/stores/useChatsTabs";
import type { CloseManyFilterScope } from "@superagents/types/api";
import type { ChatScope } from "@/features/chat/stores/useChatScope";

/** Ordem das abas na faixa (e do slide entre elas). */
export const CHAT_TABS: readonly StatusTabValue[] = [
  "human",
  "awaiting_human",
  "ai",
] as const;

export const CHAT_TAB_LABELS: Record<TabValue, string> = {
  human: "Em atendimento",
  awaiting_human: "Aguardando",
  ai: "Com a IA",
  // SA-1369: Encerradas vem do sub-item do menu (sem aba na faixa); o rótulo
  // serve ao selo da busca e ao aviso "Mostrando em Encerradas".
  closed: "Encerradas",
};

/** Ordem do slide entre as listas: Encerradas fica depois das três abas. */
export const CHAT_LIST_KEYS_ORDER: readonly TabValue[] = [
  ...CHAT_TABS,
  "closed",
];

/**
 * `id` do painel da lista (`role="tabpanel"`), alvo do `aria-controls`. É o
 * contêiner de rolagem da lista, que o InfiniteScroll também acha por ele.
 */
export const CHAT_TABPANEL_ID = "chat-list-container";

/** `id` do botão da aba (o painel se rotula por ele). */
export const chatTabId = (tab: TabValue): string => `chat-tab-${tab}`;

/**
 * A busca cruza as abas e as Encerradas (SA-1369): uma query só, com os quatro
 * status. O servidor aplica a visibilidade das encerradas por área.
 */
export const SEARCH_ATTENDANT_FILTERS: AttendantStatus[] = [
  "ai",
  "awaiting_human",
  "closed",
  "human",
];

/** Aba onde a conversa vive hoje (o valor da aba é o próprio status). */
export const tabForChat = (
  chat: Pick<ChatWithLastAttendance, "attendant_status">
): TabValue => {
  switch (chat.attendant_status) {
    case "ai":
    case "awaiting_human":
    case "closed":
      return chat.attendant_status;
    default:
      return "human";
  }
};

/**
 * Recortes (Atribuídas a mim / Minhas áreas) só valem nas abas humanas: a IA
 * não tem atendimento aberto para recortar (regra da SA-1367).
 */
export const tabHasScope = (tab: TabValue): boolean =>
  tab === "human" || tab === "awaiting_human";

/**
 * Finalizar em massa existe nas abas humanas; na IA e nas Encerradas (SA-1369)
 * não há o que finalizar.
 */
export const tabHasBulk = (tab: TabValue): boolean =>
  tab === "human" || tab === "awaiting_human";

/**
 * Escopo do `close-many` em "todas da aba": o servidor encerra pelo filtro,
 * então a aba precisa de um equivalente lá (SA-1368).
 * - Aguardando: a fila (`awaiting_human`), só para o Admin.
 * - Em atendimento, quem não é Admin: os próprios atendimentos
 *   (`my_attendances`), o que o servidor deixaria essa pessoa encerrar de
 *   qualquer jeito. Vale com ou sem recorte.
 * - O resto cai em "all" (que no servidor inclui a fila) e não é oferecido
 *   (ver `canBulkCloseAllInTab`).
 */
export const bulkCloseScopeForTab = (
  tab: TabValue,
  isAdmin: boolean
): CloseManyFilterScope => {
  if (tab === "awaiting_human") return "awaiting_human";
  if (tab === "human" && !isAdmin) return "my_attendances";
  return "all";
};

/**
 * O "todas da aba" pode mostrar o total da aba? No lote dos próprios
 * atendimentos (quem não é Admin, em Em atendimento) o total só é o número que
 * o servidor encerra com "Atribuídas a mim"; fora dele a aba mistura
 * atendentes e a tela não promete número nenhum (SA-1368).
 */
export const shouldHideAllInTabCount = (
  tab: TabValue,
  isAdmin: boolean,
  scope: ChatScope
): boolean => tab === "human" && !isAdmin && scope !== "assigned";

/** Dica do contador da aba (hover). */
export const tabCountTooltip = (tab: StatusTabValue, count: number): string => {
  const one = count === 1;
  const conversas = one ? "1 conversa" : `${count} conversas`;
  switch (tab) {
    case "awaiting_human":
      return `${conversas} na fila`;
    case "ai":
      return `${conversas} com a IA`;

    default:
      return `${conversas} em atendimento`;
  }
};
